import Requester from '../../shared/Requester';

function basePath(unidadId) {
    return `${unidadId}/pld-expedientes`;
}

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            const base64 = typeof result === 'string' && result.includes(',') ? result.split(',')[1] : result;
            resolve(base64);
        };
        reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
        reader.readAsDataURL(file);
    });
}

export function getBackendUrl() {
    return process.env.REACT_APP_BACKEND || '';
}

/**
 * Convierte una ruta relativa de archivo del backend en URL absoluta para fetch.
 * @param {string} url
 */
export function toAbsoluteBackendUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (/^https?:\/\//i.test(url)) return url;
    const base = getBackendUrl().replace(/\/$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;
    return base ? `${base}${path}` : path;
}

/**
 * Construye la URL de fetch para un documento de sujeto PLD.
 *
 * Prioridades:
 * 1. Si doc.fileUrl existe → reconstruye la ruta desde cero usando REACT_APP_BACKEND
 *    (robusto ante cambios de DNS o URLs mal guardadas en Mongo).
 * 2. Si doc.url es una ruta relativa (nuevo formato) → la prepende con REACT_APP_BACKEND.
 * 3. Si doc.url es absoluta → extrae el pathname y lo redirige a REACT_APP_BACKEND.
 *
 * @param {string} unidadId
 * @param {string} sujetoId
 * @param {{ url?: string, fileUrl?: string }} doc
 * @returns {string|null}
 */
export function resolvePldDocumentoFetchUrl(unidadId, sujetoId, doc) {
    if (!doc) return null;
    const token = process.env.REACT_APP_PLD_FILES_TOKEN;

    if (doc.fileUrl && unidadId && sujetoId) {
        let path = `/${String(unidadId)}/pld-expedientes/files/${String(sujetoId)}/${encodeURIComponent(doc.fileUrl)}`;
        if (token) {
            path += `?token=${encodeURIComponent(token)}`;
        }
        return toAbsoluteBackendUrl(path);
    }

    const raw = doc.url;
    if (!raw || typeof raw !== 'string') return null;

    if (/^https?:\/\//i.test(raw)) {
        try {
            const u = new URL(raw);
            if (u.pathname.includes('/pld-expedientes/files/')) {
                let path = `${u.pathname}${u.search || ''}`;
                if (token && !u.searchParams.has('token')) {
                    path += u.search ? `&token=${encodeURIComponent(token)}` : `?token=${encodeURIComponent(token)}`;
                }
                return toAbsoluteBackendUrl(path);
            }
        } catch (e) {
            return raw;
        }
        return raw;
    }

    let path = raw.startsWith('/') ? raw : `/${raw}`;
    if (token && !path.includes('token=')) {
        path += path.includes('?') ? `&token=${encodeURIComponent(token)}` : `?token=${encodeURIComponent(token)}`;
    }
    return toAbsoluteBackendUrl(path);
}

/**
 * @param {string} unidadId
 * @param {Record<string, string>} [query] p. ej. `{ proceso_id }` para Escenario A
 */
export async function listOperaciones(unidadId, query) {
    const response = await Requester.get(`${basePath(unidadId)}/operaciones`, {
        params: query || {},
    });
    return response.data;
}

/** Lista operaciones PLD filtradas por proceso PSA (vinculación Escenario A). */
export async function listOperacionesPorProceso(unidadId, procesoId) {
    return listOperaciones(unidadId, { proceso_id: procesoId });
}

export async function createOperacion(unidadId, payload) {
    const response = await Requester.post(`${basePath(unidadId)}/operaciones`, payload);
    return response.data;
}

export async function updateOperacion(unidadId, operacionId, payload) {
    const response = await Requester.patch(`${basePath(unidadId)}/operaciones/${operacionId}`, payload);
    return response.data;
}

export async function deleteOperacion(unidadId, operacionId) {
    const response = await Requester.delete(`${basePath(unidadId)}/operaciones/${operacionId}`);
    return response.data;
}

export async function listNodosByOperacion(unidadId, operacionId) {
    const response = await Requester.get(`${basePath(unidadId)}/operaciones/${operacionId}/nodos`);
    return response.data;
}

export async function listOperacionSujetos(unidadId, operacionId) {
    const response = await Requester.get(`${basePath(unidadId)}/operaciones/${operacionId}/sujetos`);
    return response.data;
}

export async function createNodo(unidadId, operacionId, payload) {
    const response = await Requester.post(`${basePath(unidadId)}/operaciones/${operacionId}/nodos`, payload);
    return response.data;
}

export async function deleteNodo(unidadId, nodoId) {
    const response = await Requester.delete(`${basePath(unidadId)}/nodos/${nodoId}`);
    return response.data;
}

export async function updateNodo(unidadId, nodoId, payload) {
    const response = await Requester.patch(`${basePath(unidadId)}/nodos/${nodoId}`, payload);
    return response.data;
}

export async function upsertSujetoByRfc(unidadId, payload) {
    const response = await Requester.post(`${basePath(unidadId)}/sujetos/upsert-rfc`, payload);
    return response.data;
}

export async function getSujetoById(unidadId, sujetoId) {
    const response = await Requester.get(`${basePath(unidadId)}/sujetos/${sujetoId}`);
    return response.data;
}

export async function updateSujeto(unidadId, sujetoId, payload) {
    const response = await Requester.patch(`${basePath(unidadId)}/sujetos/${sujetoId}`, payload);
    return response.data;
}

export async function getNodoBySujetoId(unidadId, sujetoId) {
    const response = await Requester.get(`${basePath(unidadId)}/nodos/by-sujeto/${sujetoId}`);
    return response.data;
}

export async function uploadDocumento(unidadId, sujetoId, docId, file) {
    const base64 = await fileToBase64(file);
    const response = await Requester.post(`${basePath(unidadId)}/documentos/upload`, {
        sujetoId,
        docId,
        fileName: file.name,
        contentType: file.type || null,
        base64,
    });
    return response.data;
}

export async function uploadDocumentoOperacion(unidadId, operacionId, docId, file, extraPayload) {
    const base64 = await fileToBase64(file);
    const response = await Requester.post(
        `${basePath(unidadId)}/operaciones/${operacionId}/documentos/upload`,
        {
            docId,
            fileName: file.name,
            contentType: file.type || null,
            base64,
            ...(extraPayload || {}),
        }
    );
    return response.data;
}

export async function deleteComprobantePago(unidadId, operacionId, idx) {
    const response = await Requester.delete(
        `${basePath(unidadId)}/operaciones/${operacionId}/comprobantes-pago/${idx}`
    );
    return response.data;
}

/**
 * Construye la URL de fetch para un documento de operación PLD (vista_aviso / acuse_aviso).
 *
 * Mismas prioridades que resolvePldDocumentoFetchUrl:
 * 1. doc.fileUrl → reconstruye ruta desde cero con REACT_APP_BACKEND.
 * 2. doc.url relativa → prepende REACT_APP_BACKEND.
 * 3. doc.url absoluta → extrae pathname y redirige a REACT_APP_BACKEND.
 *
 * @param {string} unidadId
 * @param {string} operacionId
 * @param {{ url?: string, fileUrl?: string }} doc
 * @returns {string|null}
 */
export function resolveOperacionDocumentoFetchUrl(unidadId, operacionId, doc) {
    if (!doc) return null;
    const token = process.env.REACT_APP_PLD_FILES_TOKEN;

    if (doc.fileUrl && unidadId && operacionId) {
        let path = `/${String(unidadId)}/pld-expedientes/operaciones/${String(operacionId)}/files/${encodeURIComponent(doc.fileUrl)}`;
        if (token) {
            path += `?token=${encodeURIComponent(token)}`;
        }
        return toAbsoluteBackendUrl(path);
    }

    const raw = doc.url;
    if (!raw || typeof raw !== 'string') return null;

    if (/^https?:\/\//i.test(raw)) {
        try {
            const u = new URL(raw);
            if (u.pathname.includes('/pld-expedientes/operaciones/')) {
                let path = `${u.pathname}${u.search || ''}`;
                if (token && !u.searchParams.has('token')) {
                    path += u.search ? `&token=${encodeURIComponent(token)}` : `?token=${encodeURIComponent(token)}`;
                }
                return toAbsoluteBackendUrl(path);
            }
        } catch (e) {
            return raw;
        }
        return raw;
    }

    let path = raw.startsWith('/') ? raw : `/${raw}`;
    if (token && !path.includes('token=')) {
        path += path.includes('?') ? `&token=${encodeURIComponent(token)}` : `?token=${encodeURIComponent(token)}`;
    }
    return toAbsoluteBackendUrl(path);
}

export async function listPaises(unidadId) {
    const response = await Requester.get(`${basePath(unidadId)}/catalogos/paises`);
    return response.data;
}

/** Catálogo de jurisdicciones GAFI (ISO alpha-3 → nombre). */
export async function listPaisesGafi() {
    const response = await Requester.get('caracteristicas-inherentes/paises-gafi');
    return (response.data && response.data.paises) || {};
}

/**
 * Obtiene el registro más reciente de características inherentes (EBR)
 * de un sujeto en una operación específica.
 * @param {string} operacionId
 * @param {string} sujetoId
 * @returns {Object|null} registro o null si no existe
 */
export async function getEbrPorSujeto(operacionId, sujetoId) {
    const response = await Requester.get('caracteristicas-inherentes/historial', {
        params: { operacion_id: operacionId, sujeto_id: sujetoId },
    });
    const registros = response.data && response.data.registros;
    return Array.isArray(registros) && registros.length > 0 ? registros[0] : null;
}
